import { useEffect, useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  QrCode, LogOut, RefreshCw, CheckCircle2, XCircle, Copy, Check, Send, Zap, ShieldCheck, Info,
} from "lucide-react";

/**
 * WhatsApp Bridge Console.
 *  - Shows current status (disconnected / connecting / qr / open)
 *  - Renders the QR image the bridge emits, refreshing every 3s
 *  - After pairing, reveals { session_id, phone, api_token, endpoint }
 *  - Test-send form so admin can validate a real number before running a campaign
 */
export default function AdminWhatsappBridge() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState("Hello from HTP Sports Hub — bridge test.");
  const [copied, setCopied] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/whatsapp/bridge/status"); setState(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPairing = async () => {
    setBusy(true);
    try {
      await api.post("/admin/whatsapp/bridge/connect");
      toast.success("Pairing session started. Scan the QR now.");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Log out and clear the current WhatsApp pairing?")) return;
    setBusy(true);
    try { await api.post("/admin/whatsapp/bridge/logout"); toast.success("Disconnected."); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const testSend = async () => {
    if (!testTo || !testMsg) { toast.error("Number + message required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/whatsapp/bridge/test-send", { to: testTo, text: testMsg });
      toast.success(`Sent · id ${data.id?.slice(0, 8) || "-"}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const copy = async (label, value) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1200);
  };

  const status = state?.status || "loading";
  const isOpen = status === "open";
  const creds = state?.credentials;

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-2">// Messaging · In-house Bridge</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter">WhatsApp Bridge Console</h1>
        <p className="text-sm text-white/60 mt-2 max-w-2xl">
          Scan a QR from your dedicated WhatsApp Business phone once. Every message our platform triggers — renewal
          reminders, wallet statements, campaigns — will then go through <b>your</b> number, from <b>your</b> account.
        </p>
      </div>

      {/* Safety banner */}
      <div className="border border-yellow-500/40 bg-yellow-950/20 p-4 mb-6 flex gap-3 text-xs" data-testid="bridge-safety">
        <ShieldCheck size={18} className="text-yellow-300 shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-yellow-300 uppercase text-[11px] tracking-widest mb-1">Anti-ban safeguards</div>
          <ul className="text-white/70 space-y-0.5 list-disc pl-4">
            <li>Rate limit set per campaign (max 100/hr, default 20/hr)</li>
            <li>Human-like jitter · quiet hours 22:00–08:00 IST</li>
            <li>Auto-pause after 3 failed sends</li>
            <li>External API rate-capped at 30 msgs/min per token</li>
          </ul>
        </div>
      </div>

      {/* Android setup helper — visible only when disconnected */}
      {!isOpen && (
        <div className="border border-brand-blue/40 bg-brand-blue/8 p-5 mb-6" data-testid="android-setup-helper">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-2">// Host the bridge on Android</div>
          <p className="text-sm text-white/80 leading-snug">
            The bridge must run from a residential IP (WhatsApp blocks cloud-server pairing).
            The easiest free path is <b>Termux on an Android phone</b>. Setup takes ~15 minutes.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href="/api/public/wa-bridge/README.md"
              target="_blank" rel="noreferrer"
              data-testid="android-readme-link"
              className="text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white flex items-center gap-1"
            >
              📖 Step-by-step README
            </a>
            <a
              href="/api/public/wa-bridge/setup.sh"
              download="wa-bridge-setup.sh"
              data-testid="android-script-link"
              className="text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white flex items-center gap-1"
            >
              ⬇ Setup script (setup.sh)
            </a>
          </div>
          <div className="mt-3 bg-black/50 border border-white/10 p-3 font-mono text-[11px] text-green-300 overflow-x-auto">
            {"# In Termux on your Android phone, paste this one command:\n"}
            curl -fsSL https://htpsportshub.in/api/public/wa-bridge/setup.sh | bash
          </div>
        </div>
      )}

      {/* Status card */}
      <div className={`border-2 p-6 mb-6 ${
        isOpen ? "border-green-500/60 bg-green-950/20" :
        status === "qr" ? "border-brand-orange bg-brand-orange/10" :
        "border-white/12 bg-[#131A2B]"
      }`} data-testid="bridge-status-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            {isOpen ? <CheckCircle2 size={24} className="text-green-400" />
              : status === "qr" ? <QrCode size={24} className="brand-orange" />
              : <XCircle size={24} className="text-white/40" />}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Status</div>
              <div className="font-display font-black uppercase text-xl">
                {isOpen ? "Connected"
                  : status === "qr" ? "Waiting for scan"
                  : status === "connecting" ? "Connecting…"
                  : "Disconnected"}
              </div>
              {isOpen && state?.phone && (
                <div className="text-xs text-white/60 mt-1 font-mono">
                  +{state.phone} · online since {new Date(state.connected_at).toLocaleString("en-IN")}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="bridge-refresh" className="border border-white/20 px-3 py-2 text-xs font-mono uppercase tracking-widest hover:bg-white/10 flex items-center gap-2">
              <RefreshCw size={12} /> Refresh
            </button>
            {!isOpen && (
              <button onClick={startPairing} disabled={busy} data-testid="bridge-connect" className="bg-brand-orange text-black px-4 py-2 font-bold text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                <QrCode size={12} /> {status === "qr" ? "Regenerate QR" : "Connect via QR"}
              </button>
            )}
            {isOpen && (
              <button onClick={disconnect} disabled={busy} data-testid="bridge-logout" className="border border-red-500/50 text-red-400 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-500 hover:text-white flex items-center gap-2">
                <LogOut size={12} /> Disconnect
              </button>
            )}
          </div>
        </div>

        {/* QR display */}
        {status === "qr" && state?.qr && (
          <div className="mt-6 flex flex-col md:flex-row gap-6 items-start" data-testid="bridge-qr-display">
            <img src={state.qr} alt="Scan with WhatsApp" className="w-64 h-64 bg-white p-2 shrink-0" data-testid="bridge-qr-image" />
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-2">// Pairing instructions</div>
              <ol className="text-sm text-white/80 space-y-2 list-decimal pl-4">
                <li>Open <b>WhatsApp Business</b> on the dedicated office phone</li>
                <li>Tap <b>Settings → Linked devices → Link a device</b></li>
                <li>Scan the QR shown here</li>
                <li>Wait a few seconds — this page updates automatically</li>
              </ol>
              <div className="mt-4 text-[11px] text-white/50 flex items-start gap-1.5">
                <Info size={13} className="shrink-0 mt-0.5" />
                <span>The QR refreshes every ~20s. If you missed it, click <b>Regenerate QR</b>.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Credentials — visible only when open */}
      {isOpen && creds && (
        <div className="border border-brand-blue/50 bg-brand-blue/8 p-6 mb-6" data-testid="bridge-credentials">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-2">// Use from other websites</div>
          <h3 className="font-display text-xl font-black uppercase mb-3">Integration Credentials</h3>
          <p className="text-xs text-white/60 mb-4 max-w-2xl">
            Use these to <b>send WhatsApp messages from any other website/backend</b> via the same paired number.
            One HTTP call, no SDK. Rotate by disconnecting and reconnecting.
          </p>

          <CredRow label="Session ID"  value={creds.session_id} testid="cred-session" onCopy={(v) => copy("session", v)} copied={copied === "session"} />
          <CredRow label="Phone number" value={`+${creds.phone}`} testid="cred-phone"   onCopy={(v) => copy("phone", v)}   copied={copied === "phone"} />
          <CredRow label="API Token"   value={creds.api_token} testid="cred-token"    onCopy={(v) => copy("token", v)}   copied={copied === "token"}  secret />
          <CredRow label="Endpoint"    value={creds.endpoint}  testid="cred-endpoint" onCopy={(v) => copy("endpoint", v)} copied={copied === "endpoint"} />

          <div className="mt-4 border-t border-white/10 pt-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Example · send from external site</div>
            <pre className="bg-black/60 border border-white/10 p-3 text-[11px] font-mono text-green-300 overflow-x-auto rounded" data-testid="cred-example">
{`curl -X POST ${creds.endpoint || "https://your-domain.com/api/wa-bridge/v1/send"} \\
  -H "Authorization: Bearer ${creds.api_token || "<TOKEN>"}" \\
  -H "Content-Type: application/json" \\
  -d '{"to":"+919812345678","text":"Hello from partner site"}'`}
            </pre>
          </div>
        </div>
      )}

      {/* Test-send */}
      {isOpen && (
        <div className="border border-white/12 bg-[#131A2B] p-5 mb-8" data-testid="bridge-test-send">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">// Send a one-off test</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="+91XXXXXXXXXX or 10 digits" data-testid="bridge-test-to"
              className="bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-brand-orange" />
            <button onClick={testSend} disabled={busy} data-testid="bridge-test-btn"
              className="bg-brand-blue text-white px-4 py-2.5 font-bold text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
              <Send size={12} /> Send test
            </button>
          </div>
          <textarea value={testMsg} onChange={(e) => setTestMsg(e.target.value)}
            data-testid="bridge-test-msg" rows={2}
            className="w-full mt-3 bg-black/40 border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-orange" />
        </div>
      )}
    </div>
  );
}

function CredRow({ label, value, testid, onCopy, copied, secret = false }) {
  return (
    <div className="flex items-center gap-3 mb-2" data-testid={testid}>
      <div className="w-32 shrink-0 text-xs font-mono uppercase tracking-widest text-white/50">{label}</div>
      <div className="flex-1 font-mono text-sm bg-black/40 border border-white/12 px-3 py-2 select-all break-all">
        {value ? (secret ? `${value.slice(0, 8)}…${value.slice(-4)}` : value) : "—"}
      </div>
      <button onClick={() => onCopy(value)} disabled={!value}
        className="text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-2 hover:bg-white/10 disabled:opacity-40 flex items-center gap-1 shrink-0">
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}
